import { Box, TextInput, Typography } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import type { UserDetail } from "@dealer-center/ddc-pages-configuration-interface";
import { USER_INFO_FIELD_IDS } from "./fieldIds";
import { FIELD_GRID, FULL_WIDTH_FIELD } from "./fieldGrid";
import type { UserInfoEdits } from "./useUserInfoEdits";
import {
  type UserInfoFieldMessages,
  userInfoFieldValidation
} from "./validateUserInfo";

export interface GeneralInformationProps {
  summary: UserDetail;
  edits: UserInfoEdits;
  /** Opts the fields into validation; both pages always pass it. See `userInfoFieldValidation`. */
  errors?: UserInfoFieldMessages;
  isReadOnly?: boolean;
}

/**
 * FR-012's four fields: Title, First Name, Last Name, Company.
 *
 * **First Name, Last Name, and Company are required; Title is not** (FR-020) — and these three are
 * the only required fields anywhere on this form. Interstate renders the requirement as the text
 * "(required)" inside the accessible label, so it is not conveyed by colour alone (FR-050). Optional
 * fields carry **no** "(optional)" tag: most fields on this tab are optional, so tagging them adds
 * noise without information.
 *
 * **Every field is editable** as of 2026-08-31, superseding FR-052's "non-editable" bullet. A user can
 * type, and what they type survives a tab switch (FR-009). Committing it is the page's business, not
 * this component's: both pages save through `useUserInfoSave`. Disabling the inputs themselves was the
 * stricter reading of
 * FR-052 and it made the tab read as "you may not change this", which is not true. `disabled` inputs
 * also cannot become dirty, which is what blocked FR-008a's real rule from ever being implemented
 * (see `HeaderActions`).
 *
 * Values come from `edits.valueOf`, which layers pending edits over the fetched record. The state
 * lives above the tab panels because this component unmounts on every tab switch — see
 * `useUserInfoEdits`.
 *
 * **Every field needs an explicit `id`.** Without one Interstate falls back to the constant
 * `interstate-text-input`, so all ten fields on this tab share it: the `<input>` gets no `id` at all
 * and every `<label for="interstate-text-input">` points at nothing. That breaks FR-050's "every
 * field labelled" for real — a screen reader announces an unlabelled edit field — and it is invisible
 * unless a test queries by label, which is why the specs do.
 */
export const GeneralInformation = ({
  summary,
  edits,
  errors,
  isReadOnly = false
}: GeneralInformationProps) => (
  <Box component={"section"} data-testid={"general-information"}>
    <Typography tag={"h2"} variant={"h4"}>
      {Locale.getText("GENERAL_INFORMATION") as string}
    </Typography>

    {/*
     * Not a uniform 2-up grid. Title and Company span the full width, so the row that pairs two
     * fields pairs the two that belong together — First Name beside Last Name. Left uniform, the
     * grid reads as Title│First Name and Last Name│Company, splitting the name across two rows and
     * pairing each half with something unrelated.
     *
     * `1 / -1` rather than a breakpoint-conditional span: at `xs` the grid is already one column, so
     * first-to-last-line is that single column and the rule needs no breakpoint of its own.
     */}
    <Box sx={FIELD_GRID}>
      <TextInput
        {...userInfoFieldValidation("title", errors)}
        data-testid={"field-title"}
        id={USER_INFO_FIELD_IDS.title}
        label={Locale.getText("COMPOSER_TITLE") as string}
        name={"title"}
        readOnly={isReadOnly}
        sx={FULL_WIDTH_FIELD}
        value={edits.valueOf(summary, "title")}
        onChange={event => edits.setField("title", event.target.value)}
      />
      <TextInput
        {...userInfoFieldValidation("firstName", errors)}
        required
        data-testid={"field-first-name"}
        id={USER_INFO_FIELD_IDS.firstName}
        label={Locale.getText("FIRST_NAME") as string}
        name={"firstName"}
        readOnly={isReadOnly}
        value={edits.valueOf(summary, "firstName")}
        onChange={event => edits.setField("firstName", event.target.value)}
      />
      <TextInput
        {...userInfoFieldValidation("lastName", errors)}
        required
        data-testid={"field-last-name"}
        id={USER_INFO_FIELD_IDS.lastName}
        label={Locale.getText("LAST_NAME") as string}
        name={"lastName"}
        readOnly={isReadOnly}
        value={edits.valueOf(summary, "lastName")}
        onChange={event => edits.setField("lastName", event.target.value)}
      />
      <TextInput
        {...userInfoFieldValidation("company", errors)}
        required
        data-testid={"field-company"}
        id={USER_INFO_FIELD_IDS.company}
        label={Locale.getText("COMPANY") as string}
        name={"company"}
        readOnly={isReadOnly}
        sx={FULL_WIDTH_FIELD}
        value={edits.valueOf(summary, "company")}
        onChange={event => edits.setField("company", event.target.value)}
      />
    </Box>
  </Box>
);
